import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { EmptyState, ErrorState, PageHeader, SkeletonList } from '@/components/ui';
import { CategoryProgress } from '@/components/CategoryCard';
import { SignListItem } from '@/components/SignListItem';
import { useCategory, useSignStates, useSignsOfCategory } from '@/hooks/useCatalogQueries';
import { CategoryDownloadButton } from '@/features/downloads/CategoryDownloadButton';

export function CategoryPage(): React.ReactElement {
  const { categoryId } = useParams<{ categoryId: string }>();

  const category = useCategory(categoryId);
  const signs = useSignsOfCategory(categoryId);

  const signIds = useMemo(() => (signs ?? []).map((sign) => sign.id), [signs]);
  const states = useSignStates(signIds);

  if (category === undefined || signs === undefined) {
    return (
      <>
        <PageHeader title="Categoria" backTo="/" />
        <SkeletonList rows={8} />
      </>
    );
  }

  if (category === null) {
    return (
      <>
        <PageHeader title="Categoria" backTo="/" />
        <ErrorState
          title="Categoria não encontrada"
          description="Ela pode ter saído do glossário na última atualização."
        />
      </>
    );
  }

  const learned = signIds.filter((id) => states?.get(id)?.learned).length;
  const offline = signIds.filter((id) => states?.get(id)?.offline.status === 'available').length;

  // Sinais removidos da fonte continuam no banco como inativos. Ficam no fim da
  // lista em vez de sumirem, porque o usuário pode ter favoritado ou baixado.
  const visible = [...signs].sort((a, b) => Number(b.active) - Number(a.active) || a.order - b.order);

  return (
    <>
      <PageHeader
        title={category.name}
        backTo="/"
        subtitle={
          <span className="flex flex-wrap gap-x-2">
            <span>{category.actualCount} sinais</span>
            {learned > 0 && <span className="text-emerald-400">{learned} aprendidos</span>}
            {offline > 0 && <span className="text-sky-400">{offline} offline</span>}
          </span>
        }
      />

      {/* Só aparece quando há progresso: uma barra zerada lê como divisor solto. */}
      {learned > 0 && <CategoryProgress learned={learned} total={signs.length} />}

      <CategoryDownloadButton categoryId={category.id} signs={signs} offlineCount={offline} />

      {signs.length === 0 ? (
        <EmptyState title="Nenhum sinal nesta categoria" />
      ) : (
        <ul className="mt-3 flex flex-col pb-4">
          {visible.map((sign) => (
            <SignListItem key={sign.id} sign={sign} state={states?.get(sign.id)} />
          ))}
        </ul>
      )}
    </>
  );
}
